import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Heart,
  ImageOff,
  IndianRupee,
  LoaderCircle,
  Minus,
  Plus,
  ShieldCheck,
  Trash2,
  Truck,
  X,
} from "lucide-react";
import { formatINR, resolveProductImage } from "../../lib/utils-cynos";
import { useCoupon } from "../../context/CouponContext";

const itemPendingKey = (item) =>
  `${String(item.product_id || "")}::${String(item.size || "").toLowerCase()}::${String(item.color || "").toLowerCase()}`;

const variantOptions = (options = [], selected = "") => {
  const cleaned = Array.from(new Set((Array.isArray(options) ? options : []).filter(Boolean)));
  if (selected && !cleaned.some((option) => String(option).toLowerCase() === String(selected).toLowerCase())) {
    cleaned.unshift(selected);
  }
  return cleaned;
};

const itemDescription = (item) =>
  item.short_description ||
  item.description ||
  item.subtitle ||
  item.category ||
  "Premium CYNOS streetwear essential.";

const ProductThumbnail = ({ item, className = "", imageClassName = "object-cover object-center" }) => {
  const image = resolveProductImage(item.image);

  return (
    <div className={`relative overflow-hidden rounded-[14px] border border-white/10 bg-black ${className}`}>
      <ImageOff className="absolute inset-0 m-auto h-5 w-5 text-zinc-700" aria-hidden="true" />
      {image && (
        <img
          src={image}
          alt={item.name}
          loading="lazy"
          className={`relative z-10 h-full w-full ${imageClassName}`}
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      )}
    </div>
  );
};

const VariantSelect = ({ label, value, options, disabled, onChange }) => {
  const values = variantOptions(options, value);

  return (
    <label className="min-w-0">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
        {label}
      </span>
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || values.length === 0}
        aria-label={label}
        className="h-11 w-full rounded-[10px] border border-white/10 bg-[#202020] px-2 text-[11px] font-semibold uppercase text-white outline-none transition focus:border-cynos-red focus:ring-2 focus:ring-cynos-red/20 disabled:cursor-not-allowed disabled:opacity-60 min-[390px]:px-3 min-[390px]:text-xs"
      >
        {values.length === 0 && <option value="">--</option>}
        {values.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
};

const Notice = ({ children, tone, icon: Icon }) => (
  <div
    className={`mb-3 flex items-start gap-2 rounded-[10px] border p-3 font-mono text-[10px] uppercase tracking-[0.12em] ${
      tone === "error"
        ? "border-red-500/30 bg-red-500/10 text-red-300"
        : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
    }`}
  >
    {Icon && <Icon size={12} className="mt-0.5 shrink-0" />}
    <span>{children}</span>
  </div>
);

const TotalRow = ({ label, value, strong = false, red = false }) => (
  <div className="flex items-center justify-between gap-4">
    <span
      className={`font-mono text-[11px] uppercase tracking-[0.16em] ${
        strong ? "text-white" : "text-zinc-500"
      }`}
    >
      {label}
    </span>
    <span
      className={`text-right ${
        strong ? "font-anton text-2xl text-white" : red ? "font-semibold text-cynos-red" : "font-semibold text-white"
      }`}
    >
      {value}
    </span>
  </div>
);

const MobileCartItem = ({
  item,
  pending,
  onRemoveItem,
  onMoveToWishlist,
  onUpdateQuantity,
  onVariantChange,
}) => {
  const itemTotal = Number(item.price || 0) * Number(item.qty || 1);

  return (
    <motion.article
      layout
      key={`${item.product_id}-${item.size}-${item.color || ""}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.992 }}
      className="rounded-[16px] border border-white/10 bg-[#181818] p-4 shadow-[0_18px_55px_rgba(0,0,0,0.28)]"
    >
      <div className="grid grid-cols-[minmax(0,1.42fr)_minmax(0,1fr)] gap-3">
        <ProductThumbnail
          item={item}
          className="h-[260px] w-full min-[390px]:h-[286px]"
          imageClassName="object-cover object-center"
        />

        <div className="flex h-[260px] min-w-0 flex-col min-[390px]:h-[286px]">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="line-clamp-2 min-w-0 text-[16px] font-extrabold uppercase leading-[1.08] text-white min-[390px]:text-[17px]">
                {item.name}
              </h4>
              <motion.span
                key={itemTotal}
                initial={{ opacity: 0.5, y: -3 }}
                animate={{ opacity: 1, y: 0 }}
                className="shrink-0 text-right text-xl font-black leading-none text-cynos-red"
              >
                ₹{formatINR(itemTotal)}
              </motion.span>
            </div>

            <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-zinc-400">
              {itemDescription(item)}
            </p>
          </div>

          <div className="mt-auto space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <VariantSelect
                label="Size"
                value={item.size || ""}
                options={item.sizes}
                disabled={pending}
                onChange={(nextSize) => onVariantChange?.(item, nextSize, item.color || "")}
              />
              <VariantSelect
                label="Color"
                value={item.color || ""}
                options={item.colors}
                disabled={pending}
                onChange={(nextColor) => onVariantChange?.(item, item.size || "", nextColor)}
              />
            </div>

            <div className="grid grid-cols-3 gap-2" aria-label={`${item.name} quantity controls`}>
              <button
                type="button"
                onClick={() => onUpdateQuantity(item.product_id, item.size, item.color, item.qty - 1)}
                disabled={pending || item.qty <= 1}
                className="flex h-[46px] items-center justify-center rounded-[10px] border border-white/10 bg-[#202020] text-white transition hover:border-white/25 hover:bg-white/10 active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label={`Decrease ${item.name} quantity`}
              >
                <Minus size={18} />
              </button>

              <div className="flex h-[46px] items-center justify-center rounded-[10px] border border-white/10 bg-black/40 font-mono text-base font-bold text-white">
                {pending ? (
                  <LoaderCircle size={16} className="animate-spin text-cynos-red" aria-label="Updating quantity" />
                ) : (
                  item.qty
                )}
              </div>

              <button
                type="button"
                onClick={() => onUpdateQuantity(item.product_id, item.size, item.color, item.qty + 1)}
                disabled={pending}
                className="flex h-[46px] items-center justify-center rounded-[10px] border border-white/10 bg-[#202020] text-white transition hover:border-cynos-red hover:bg-cynos-red/10 hover:text-cynos-red active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label={`Increase ${item.name} quantity`}
              >
                <Plus size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onRemoveItem(item.product_id, item.size, item.color)}
                disabled={pending}
                className="flex h-[46px] items-center justify-center gap-1.5 rounded-[10px] border border-white/10 bg-[#202020] text-[10px] font-semibold uppercase tracking-tight text-zinc-200 transition hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 min-[390px]:text-xs"
                aria-label={`Delete ${item.name}`}
              >
                <Trash2 size={15} /> Delete
              </button>

              <button
                type="button"
                onClick={() => onMoveToWishlist(item)}
                disabled={pending}
                className="flex h-[46px] items-center justify-center gap-1.5 rounded-[10px] border border-white/10 bg-[#202020] px-1 text-center text-[10px] font-semibold uppercase leading-tight text-zinc-200 transition hover:border-white/30 hover:bg-white/10 hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 min-[390px]:text-xs"
                aria-label={`Move ${item.name} to wishlist`}
              >
                <Heart size={15} /> Wishlist
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const DesktopCartItem = ({
  item,
  pending,
  onRemoveItem,
  onMoveToWishlist,
  onUpdateQuantity,
  onVariantChange,
}) => (
  <motion.article
    layout
    key={`${item.product_id}-${item.size}-${item.color || ""}`}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.98 }}
    className="rounded-[14px] border border-white/10 bg-white/[0.03] p-5 transition-all hover:border-white/20 hover:bg-white/[0.05] lg:p-6"
  >
    <div className="flex flex-col gap-5 lg:flex-row">
      <ProductThumbnail item={item} className="h-64 w-full lg:h-20 lg:w-20 lg:shrink-0" />

      <div className="min-w-0 flex-1 lg:flex lg:flex-col lg:justify-between">
        <div className="mb-5 lg:mb-3">
          <h4 className="mb-2 line-clamp-2 text-lg font-bold uppercase leading-snug text-white lg:text-sm">
            {item.name}
          </h4>
          <motion.span
            key={`${item.product_id}-${item.qty}-${item.price}`}
            initial={{ opacity: 0.5, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block font-anton text-2xl font-bold text-cynos-red lg:text-xl"
          >
            ₹{formatINR(item.price * item.qty)}
          </motion.span>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3">
          <VariantSelect
            label="Size"
            value={item.size || ""}
            options={item.sizes}
            disabled={pending}
            onChange={(nextSize) => onVariantChange?.(item, nextSize, item.color || "")}
          />
          <VariantSelect
            label="Color"
            value={item.color || ""}
            options={item.colors}
            disabled={pending}
            onChange={(nextColor) => onVariantChange?.(item, item.size || "", nextColor)}
          />
        </div>

        <div className="mb-5">
          <label className="mb-2 block text-xs uppercase tracking-[0.12em] text-zinc-500">Quantity</label>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onUpdateQuantity(item.product_id, item.size, item.color, item.qty - 1)}
              disabled={pending || item.qty <= 1}
              className="flex h-12 flex-1 items-center justify-center rounded-[10px] border border-white/15 bg-white/5 text-lg text-zinc-300 transition hover:border-white/30 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
              aria-label={`Decrease ${item.name} quantity`}
            >
              <Minus size={18} />
            </button>

            <div className="flex h-12 w-16 items-center justify-center rounded-[10px] border border-white/10 bg-white/[0.05] font-mono text-lg font-bold text-white">
              {pending ? (
                <LoaderCircle size={14} className="animate-spin text-cynos-red" aria-label="Updating quantity" />
              ) : (
                item.qty
              )}
            </div>

            <button
              type="button"
              onClick={() => onUpdateQuantity(item.product_id, item.size, item.color, item.qty + 1)}
              disabled={pending}
              className="flex h-12 flex-1 items-center justify-center rounded-[10px] border border-white/15 bg-white/5 text-lg text-white transition hover:border-cynos-red hover:bg-cynos-red/10 hover:text-cynos-red disabled:cursor-not-allowed disabled:opacity-35"
              aria-label={`Increase ${item.name} quantity`}
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onRemoveItem(item.product_id, item.size, item.color)}
            disabled={pending}
            className="flex h-12 items-center justify-center gap-2 rounded-[10px] border border-white/10 bg-white/5 font-semibold uppercase text-zinc-300 transition hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Delete item"
          >
            <Trash2 size={16} /> Delete
          </button>

          <button
            type="button"
            onClick={() => onMoveToWishlist(item)}
            disabled={pending}
            className="flex h-12 items-center justify-center gap-2 rounded-[10px] border border-white/10 bg-white/5 font-semibold uppercase text-zinc-300 transition hover:border-white/30 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Move to wishlist"
          >
            <Heart size={16} /> Wishlist
          </button>
        </div>
      </div>
    </div>
  </motion.article>
);

const CouponBlock = ({
  appliedCoupon,
  couponInput,
  setCouponInput,
  couponLoading,
  couponError,
  couponMessage,
  handleApplyCoupon,
  removeCoupon,
  mobile = false,
}) => (
  <div className={mobile ? "rounded-[16px] border border-white/10 bg-[#181818] p-4" : ""}>
    {mobile && (
      <h3 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
        Coupon Code
      </h3>
    )}

    {!appliedCoupon ? (
      <div className={mobile ? "flex gap-2" : "mb-6 flex items-center gap-2"}>
        <input
          value={couponInput}
          onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleApplyCoupon();
            }
          }}
          disabled={couponLoading}
          className={`min-w-0 flex-1 rounded-[10px] border border-white/15 bg-white/5 font-mono text-xs uppercase tracking-[0.12em] text-white placeholder-zinc-600 outline-none transition focus:border-cynos-red focus:ring-2 focus:ring-cynos-red/20 disabled:opacity-50 ${
            mobile ? "h-12 px-3" : "px-3 py-2"
          }`}
          placeholder="Coupon code"
          data-testid="discount-input"
        />
        <button
          type="button"
          onClick={handleApplyCoupon}
          disabled={couponLoading || !couponInput.trim()}
          className={`rounded-[10px] border border-white/15 font-mono text-xs font-bold uppercase tracking-[0.14em] text-white transition hover:border-white/30 hover:bg-white/5 active:scale-95 disabled:opacity-50 ${
            mobile ? "h-12 px-5" : "px-3 py-2"
          }`}
        >
          {couponLoading ? <LoaderCircle size={12} className="animate-spin" /> : "Apply"}
        </button>
      </div>
    ) : (
      <div className={mobile ? "flex items-center justify-between rounded-[12px] border border-emerald-500/30 bg-emerald-500/10 p-3" : "mb-6 flex items-center justify-between rounded-[8px] border border-emerald-500/30 bg-emerald-500/10 p-3"}>
        <div>
          <p className="font-mono text-xs font-semibold uppercase text-emerald-300">{appliedCoupon.code}</p>
          <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-emerald-400">Applied</p>
        </div>
        <button
          type="button"
          onClick={() => {
            removeCoupon();
            setCouponInput("");
          }}
          className="p-1 text-emerald-300 transition hover:text-white"
          aria-label="Remove coupon"
        >
          <X size={14} />
        </button>
      </div>
    )}

    {couponError && (
      <div className={mobile ? "mt-3" : ""}>
        <Notice tone="error" icon={AlertCircle}>
          {couponError}
        </Notice>
      </div>
    )}
    {couponMessage && appliedCoupon && (
      <div className={mobile ? "mt-3" : ""}>
        <Notice tone="success">{couponMessage}</Notice>
      </div>
    )}
  </div>
);

const MobileOrderSummary = ({
  items,
  subtotal,
  discount,
  effectiveCouponDiscount,
  displayTotal,
  error,
  submitting,
  paymentMethod,
  pendingKeys,
  onRemoveItem,
  onMoveToWishlist,
  onUpdateQuantity,
  onVariantChange,
  onAddFromWishlist,
  couponProps,
}) => {
  const totalDiscount = Number(discount || 0) + Number(effectiveCouponDiscount || 0);

  return (
    <section className="md:hidden">
      <button
        type="button"
        onClick={onAddFromWishlist}
        className="mb-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-[14px] border border-white/12 bg-white px-4 font-mono text-xs font-bold uppercase tracking-[0.16em] text-black shadow-[0_14px_40px_rgba(0,0,0,0.24)] transition active:scale-[0.98]"
        data-testid="checkout-add-from-wishlist"
      >
        <Heart size={16} /> Add From Wishlist
      </button>

      <div className="space-y-4">
        {items.map((item) => (
          <MobileCartItem
            key={`${item.product_id}-${item.size}-${item.color || ""}`}
            item={item}
            pending={Boolean(pendingKeys[itemPendingKey(item)])}
            onRemoveItem={onRemoveItem}
            onMoveToWishlist={onMoveToWishlist}
            onUpdateQuantity={onUpdateQuantity}
            onVariantChange={onVariantChange}
          />
        ))}
      </div>

      <div className="mt-5">
        <CouponBlock {...couponProps} mobile />
      </div>

      <div className="mt-4 rounded-[16px] border border-white/10 bg-[#181818] p-4 shadow-[0_18px_55px_rgba(0,0,0,0.25)]">
        <h3 className="mb-5 font-anton text-2xl uppercase tracking-wide text-white">Order Summary</h3>
        <div className="space-y-3">
          <TotalRow label="Subtotal" value={`₹${formatINR(subtotal)}`} />
          <TotalRow label="Shipping" value="Free" />
          <TotalRow label="Discount" value={totalDiscount > 0 ? `-₹${formatINR(totalDiscount)}` : "₹0"} red={totalDiscount > 0} />
          <div className="border-t border-white/10 pt-4">
            <TotalRow label="Grand Total" value={`₹${formatINR(displayTotal)}`} strong />
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-[12px] border border-red-500/30 bg-red-500/10 p-3 font-mono text-xs uppercase tracking-[0.12em] text-red-300">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="fixed inset-x-0 bottom-0 z-50 flex h-14 items-center justify-between rounded-t-[18px] bg-cynos-red px-5 pb-[env(safe-area-inset-bottom)] font-mono text-sm font-black uppercase tracking-[0.14em] text-white shadow-[0_-18px_45px_rgba(255,51,51,0.22)] transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-55"
        data-testid="place-order-btn"
      >
        <motion.span key={displayTotal} initial={{ opacity: 0.6, y: 3 }} animate={{ opacity: 1, y: 0 }}>
          ₹{formatINR(displayTotal)}
        </motion.span>
        <span>{submitting ? "Processing..." : paymentMethod === "phonepe" ? "Pay Now" : "Place Order"}</span>
      </button>
    </section>
  );
};

const DesktopOrderSummary = ({
  items,
  subtotal,
  discount,
  effectiveCouponDiscount,
  displayTotal,
  error,
  submitting,
  paymentMethod,
  pendingKeys,
  onRemoveItem,
  onMoveToWishlist,
  onUpdateQuantity,
  onVariantChange,
  onAddFromWishlist,
  couponProps,
}) => (
  <aside className="hidden h-fit rounded-[12px] border border-white/10 bg-white/[0.02] p-6 backdrop-blur-sm md:sticky md:top-[120px] md:block">
    <h3 className="mb-6 font-anton text-xl uppercase md:text-2xl">Order Summary</h3>

    <button
      type="button"
      onClick={onAddFromWishlist}
      className="mb-6 w-full rounded-[8px] border border-white/15 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-all hover:border-white/30 hover:bg-white/5"
      data-testid="checkout-add-from-wishlist-desktop"
    >
      <Heart size={14} className="mr-2 inline" /> Add from Wishlist
    </button>

    <div className="mb-6 max-h-96 space-y-3 overflow-y-auto pr-2">
      {items.map((item) => (
        <DesktopCartItem
          key={`${item.product_id}-${item.size}-${item.color || ""}`}
          item={item}
          pending={Boolean(pendingKeys[itemPendingKey(item)])}
          onRemoveItem={onRemoveItem}
          onMoveToWishlist={onMoveToWishlist}
          onUpdateQuantity={onUpdateQuantity}
          onVariantChange={onVariantChange}
        />
      ))}
    </div>

    <div className="mb-6 border-t border-white/10" />
    <CouponBlock {...couponProps} />

    <div className="mb-6 space-y-2 border-t border-white/10 pt-4">
      <div className="flex justify-between text-xs uppercase tracking-[0.12em]">
        <span className="text-zinc-500">Subtotal</span>
        <span className="font-semibold text-white">₹{formatINR(subtotal)}</span>
      </div>
      <div className="flex justify-between text-xs uppercase tracking-[0.12em]">
        <span className="text-zinc-500">Discount</span>
        <span className={discount > 0 ? "font-semibold text-cynos-red" : "font-semibold text-white"}>
          {discount > 0 ? `-₹${formatINR(discount)}` : "₹0"}
        </span>
      </div>
      {effectiveCouponDiscount > 0 && (
        <div className="flex justify-between text-xs uppercase tracking-[0.12em]">
          <span className="text-zinc-500">Coupon</span>
          <span className="font-semibold text-cynos-red">-₹{formatINR(effectiveCouponDiscount)}</span>
        </div>
      )}
      <div className="flex justify-between text-xs uppercase tracking-[0.12em]">
        <span className="text-zinc-500">Shipping</span>
        <span className="font-semibold text-white">Free</span>
      </div>
      <div className="flex justify-between text-xs uppercase tracking-[0.12em]">
        <span className="text-zinc-500">Tax</span>
        <span className="font-semibold text-white">₹0</span>
      </div>
    </div>

    <div className="mb-6 border-t border-white/10 pt-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs uppercase tracking-[0.16em] text-zinc-400">Grand Total</span>
        <motion.span
          key={displayTotal}
          initial={{ opacity: 0.5, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-anton text-2xl text-white"
        >
          ₹{formatINR(displayTotal)}
        </motion.span>
      </div>
    </div>

    <div className="mb-4 grid grid-cols-3 gap-2 text-center text-[9px] uppercase tracking-[0.12em]">
      <div className="rounded-[6px] border border-white/10 p-2">
        <IndianRupee size={12} className="mx-auto mb-1 text-cynos-red" />
        <span className="text-zinc-400">COD</span>
      </div>
      <div className="rounded-[6px] border border-white/10 p-2">
        <ShieldCheck size={12} className="mx-auto mb-1 text-cynos-red" />
        <span className="text-zinc-400">Secure</span>
      </div>
      <div className="rounded-[6px] border border-white/10 p-2">
        <Truck size={12} className="mx-auto mb-1 text-cynos-red" />
        <span className="text-zinc-400">Free Ship</span>
      </div>
    </div>

    <p className="mb-4 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-600">
      <Truck size={11} className="shrink-0" /> Delivery in 3-6 days
    </p>

    <button
      type="submit"
      disabled={submitting}
      className="w-full rounded-[10px] bg-cynos-red px-4 py-4 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:bg-red-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
      data-testid="place-order-btn-desktop"
    >
      {submitting
        ? "Processing..."
        : paymentMethod === "phonepe"
          ? `Pay ₹${formatINR(displayTotal)}`
          : `Place Order · ₹${formatINR(displayTotal)}`}
    </button>

    {error && (
      <div className="mt-3 rounded-[6px] border border-red-500/30 bg-red-500/10 p-3 font-mono text-xs uppercase tracking-[0.12em] text-red-300">
        {error}
      </div>
    )}
  </aside>
);

const OrderSummary = ({
  items,
  subtotal,
  discount,
  couponDiscount,
  total,
  error,
  submitting,
  paymentMethod,
  customerEmail,
  pendingKeys = {},
  onRemoveItem = () => {},
  onMoveToWishlist = () => {},
  onUpdateQuantity = () => {},
  onVariantChange = () => {},
  onAddFromWishlist = () => {},
}) => {
  const {
    appliedCoupon,
    discountAmount,
    loading: couponLoading,
    error: couponError,
    message: couponMessage,
    applyCoupon,
    removeCoupon,
  } = useCoupon();
  const [couponInput, setCouponInput] = useState("");
  const displayTotal = Math.max(0, total);
  const effectiveCouponDiscount = couponDiscount ?? discountAmount;

  useEffect(() => {
    if (appliedCoupon?.code) setCouponInput(appliedCoupon.code);
  }, [appliedCoupon]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    await applyCoupon(couponInput, subtotal, null, items, customerEmail || null);
  };

  const sharedProps = {
    items,
    subtotal,
    discount,
    effectiveCouponDiscount,
    displayTotal,
    error,
    submitting,
    paymentMethod,
    pendingKeys,
    onRemoveItem,
    onMoveToWishlist,
    onUpdateQuantity,
    onVariantChange,
    onAddFromWishlist,
    couponProps: {
      appliedCoupon,
      couponInput,
      setCouponInput,
      couponLoading,
      couponError,
      couponMessage,
      handleApplyCoupon,
      removeCoupon,
    },
  };

  return (
    <div className="min-w-0" data-testid="order-summary">
      <MobileOrderSummary {...sharedProps} />
      <DesktopOrderSummary {...sharedProps} />
    </div>
  );
};

export default OrderSummary;
